import { inject, Injectable } from '@angular/core';
import { DocumentSagaState } from '@core/core.types';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { isEqual } from 'lodash';
import { interval, switchMap, takeWhile, tap } from 'rxjs';
import {
  CheckReportsSagaApprovalState,
  ConfirmReportApproval,
  MassApproveReports,
  SAGA_STATE_NAME,
} from './report-approval-saga.actions';
import { ReportsService } from '@app/modules/reports/services/reports.service';
import { ReportSagaState } from '@app/modules/reports/types/report.types';

export interface SagaStateModel {
  activeSagas: { [key: string]: { state: ReportSagaState; id: string } };
}

@State<SagaStateModel>({
  name: SAGA_STATE_NAME,
  defaults: {
    activeSagas: {},
  },
})
@Injectable()
export class ReportApprovalSagaState {
  private reportsService = inject(ReportsService);

  @Selector()
  static getSagaById({ activeSagas }: SagaStateModel) {
    return (sagaId: string) => activeSagas[sagaId];
  }

  @Selector()
  static isSagaInProgress({ activeSagas }: SagaStateModel) {
    return (sagaId: string) => activeSagas[sagaId].state.inProgress;
  }

  @Selector()
  static isSagaReadyForApproval({ activeSagas }: SagaStateModel) {
    return (sagaId: string) =>
      !activeSagas[sagaId].state.inProgress &&
      (activeSagas[sagaId].state.isVerificationReady ||
        !!activeSagas[sagaId].state.failed);
  }

  @Selector()
  static hasSagaErrors({ activeSagas }: SagaStateModel) {
    return (sagaId: string) =>
      activeSagas[sagaId].state.warnings?.length ||
      activeSagas[sagaId].state.errors?.length;
  }

  @Action(MassApproveReports)
  massApproveReports(
    { patchState, getState, dispatch }: StateContext<SagaStateModel>,
    { reportIds, sagaId }: MassApproveReports
  ) {
    return this.reportsService.massApproveReports(reportIds).pipe(
      tap((batch: { id: string }) =>
        patchState({
          activeSagas: {
            ...getState().activeSagas,
            [sagaId]: { state: null, id: batch.id },
          },
        })
      ),
      switchMap(() =>
        interval(500).pipe(
          switchMap(() => dispatch(new CheckReportsSagaApprovalState(sagaId))),
          takeWhile(() => getState().activeSagas[sagaId]?.state?.inProgress)
        )
      )
    );
  }

  @Action(CheckReportsSagaApprovalState)
  checkApprovalState(
    { patchState, getState }: StateContext<SagaStateModel>,
    { sagaId }: CheckReportsSagaApprovalState
  ) {
    const saga = getState().activeSagas[sagaId];

    return this.reportsService.checkApprovalState(saga.id).pipe(
      tap((batch: ReportSagaState) => {
        if (!isEqual(batch, saga.state)) {
          patchState({
            activeSagas: {
              ...getState().activeSagas,
              [sagaId]: { state: batch, id: saga.id },
            },
          });
        }
      })
    );
  }

  @Action(ConfirmReportApproval)
  confirmReportApproval(
    { getState, dispatch }: StateContext<SagaStateModel>,
    { sagaId }: ConfirmReportApproval
  ) {
    const saga = getState().activeSagas[sagaId];

    return this.reportsService.confirmMassReportApproval(saga.id).pipe(
      switchMap(() =>
        interval(500).pipe(
          switchMap(() => dispatch(new CheckReportsSagaApprovalState(sagaId))),
          takeWhile(() => getState().activeSagas[sagaId].state.inProgress)
        )
      )
    );
  }
}
